import { CardLayout } from "../components/CardLayout";
import { SectionTitle } from "../components/SectionTitle";
import { TablePlot } from "../components/TablePlots";
import { HomeData } from "../TrialHomeScreen";
import { getDoseHeldReducedStacks, getIpRelatedEvents } from "../utils";

export const SafetySection = ({ data }: { data: HomeData }) => {
  const ipRelatedEvents = getIpRelatedEvents(data);
  const doseStacks = getDoseHeldReducedStacks(data);

  // Outcome and causality are optional source fields. Where a trial reports
  // neither (the oncology trials record CTCAE grades instead), show the grade
  // breakdown that does exist and drop the cards that would read as all-zero,
  // rather than implying a finding of "no Grade 3 TEAEs related to IP".
  const hasTaeOutcomes = (data.taes ?? []).length > 0;
  const hasSaeOutcomes = (data.saes ?? []).length > 0;
  const severityBreakdown = data.ae_severity ?? [];
  const hasIpRelatedData = ipRelatedEvents.some((event) =>
    Object.values(event.values ?? {}).some((count) => Number(count) > 0),
  );

  return (
    <div data-testid="trial-home-safety-section">
      <SectionTitle title="SAFETY" />
      <div className="grid grid-cols-4 gap-4">
        <div className="h-full lg:col-span-1 col-span-2">
          <CardLayout
            title="Out of Range Labs"
            subtitle="Counts of subjects for all time"
          >
            <div className="p-4">
              <TablePlot
                data={[data.outOfRange ?? []]}
                mode="counts"
                categoriesTitle="LAB"
                sorted={true}
              />
            </div>
          </CardLayout>
        </div>
        <div className="h-full lg:col-span-1 col-span-2">
          {hasTaeOutcomes ? (
            <CardLayout
              fullHeight={true}
              title="Grade 3 TEAEs Outcomes"
              subtitle="Outcomes for Grade 3 TEAEs for all time"
            >
              <div className="p-4 h-full">
                <TablePlot
                  data={[data.taes ?? []]}
                  mode="counts"
                  categoriesTitle="OUTCOME"
                />
              </div>
            </CardLayout>
          ) : (
            <CardLayout
              fullHeight={true}
              title="AEs by Severity"
              subtitle="All adverse events by reported severity, for all time"
            >
              <div className="p-4 h-full">
                <TablePlot
                  data={[severityBreakdown]}
                  mode="counts"
                  categoriesTitle="SEVERITY"
                  sorted={true}
                />
              </div>
            </CardLayout>
          )}
        </div>
        {hasSaeOutcomes && (
          <div className="h-full lg:col-span-1 col-span-2">
            <CardLayout
              fullHeight={true}
              title="SAEs Outcomes"
              subtitle="Outcomes for any grade SAEs for all time"
            >
              <div className="p-4 h-full">
                <TablePlot
                  data={[data.saes ?? []]}
                  mode="counts"
                  categoriesTitle="OUTCOME"
                  sorted={true}
                />
              </div>
            </CardLayout>
          </div>
        )}
        <div className="flex flex-col gap-4 lg:col-span-1 col-span-2">
          <CardLayout
            fullHeight={true}
            title="Doses Held & Reduced"
            subtitle="Count of doses held/reduced due to AEs"
          >
            <div className="p-4 h-full">
              <TablePlot
                data={[data.doseHeldReduced ?? []]}
                mode="counts"
                categoriesTitle="REASON"
                preDefinedStacks={doseStacks}
                stackLabels={{
                  HELD: "Held",
                  REDUCED: "Reduced",
                  "Dose Held": "Held",
                  "Dose Reduced": "Reduced",
                }}
              />
            </div>
          </CardLayout>
          {hasIpRelatedData && (
            <CardLayout
              fullHeight={true}
              title="Grade 3 TEAEs & SAEs Related to IP"
              subtitle="Count of Grade 3 TEAEs & SAEs related to IP"
            >
              <div className="p-4 h-full">
                <TablePlot
                  data={[ipRelatedEvents]}
                  mode="counts"
                  categoriesTitle="REASON"
                  noTitle
                />
              </div>
            </CardLayout>
          )}
        </div>
      </div>
    </div>
  );
};
